import React, { useState, useEffect } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import { InfoCircleTwoTone } from '@ant-design/icons';
import {
  Button, Col, message, notification, Modal, Row, Select,
  Spin, Table, Form,
} from 'antd';
import TrashCanIcon from 'mdi-react/TrashCanIcon';
import AccountIcon from 'mdi-react/AccountIcon';
import './AssociateProfile.scss';
import CustomPagination from '../../sharedComponents/customPagination/CustomPagination';
import {
  get, put, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';

const statusList = {
  ACTIVATED: {
    name: 'Ativado',
    color: '#0076ff',
  },

  DEACTIVATED: {
    name: 'Desativado',
    color: '#b2b2b2',
  },

  AWAITING_EMAIL_CONFIRMATION: {
    name: 'Aguardando confirmação de email',
    color: '#ffc107',
  },

  EXCLUSION_REQUESTED: {
    name: 'Aguardando exclusão',
    color: '#FFFF00',
  },

  EXCLUSION_APPROVED: {
    name: 'Excluído via solicitação do usuário',
    color: '#ed1515',
  },
};

const AssociateProfile = (): JSX.Element => {
  const [formInstance] = Form.useForm();
  const navigate = useNavigate();
  const matchObject = useMatch('/profile/associate/:id');

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingModalRemove, setLoadingModalRemove] = useState<boolean>(false);
  const [data, setData] = useState([]);
  const [responseAddUsers, setResponseAddUsers] = useState([]);
  const [responseRemoveUsers, setResponseRemoveUsers] = useState([]);
  const [visibleModalAddUser, setVisibleModalAddUser] = useState<boolean>(false);
  const [loadingModalUser, setLoadingModalUser] = useState<boolean>(false);
  const [userModalOptions, setUserModalOptions] = useState([]);
  const [allActivatedUsers, setAllActivatedUsers] = useState([]);
  const [visibleModalRemove, setVisibleModalRemove] = useState<boolean>(false);
  const [currentUserModalRemove, setCurrentUserModalRemove] = useState({});
  const [visibleModalCancel, setVisibleModalCancel] = useState<boolean>(false);
  const [lastPageSearched, setLastPageSearched] = useState<number>(1);
  const [state, setState] = useState<boolean>(true);

  const [paginationInfo, setPaginationInfo] = useState({
    maxSize: 15,
    numberOfPages: 1,
    pageNumber: 1,
    totalElements: 1,
  });

  const [defaultRole, setDefaultRole] = useState<boolean>(false);
  const [roleName, setRoleName] = useState<string>('');

  const fetchData = async (page: number): Promise<void> => {
    const { id } = matchObject.params;

    setLoading(true);

    if (Number.parseInt(id, 10) !== 2) {
      // se nao for o admin do sistema
      let responseFind: ResponseWrapper | undefined;
      let responseFindError: ResponseWrapper | undefined;
      let genericFindError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: `/role/find/${id}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        responseFind = await requestReference;
      } catch (error) {
        if (isSuperAgentError(error)) {
          responseFindError = error.response;
        } else {
          genericFindError = error as Error;
        }
      }

      switch (true) {
        case (responseFind?.body?.code === 3005):
          // "Role found successfully", 3005
          setRoleName(responseFind?.body.role.name);
          break;

        case (responseFindError?.body?.code === -3008):
          // "Cannot edit system admin role", -3008
          notification.error({
            message: 'Erro',
            description: 'Não é permitido alterar o perfil Administrador do Sistema',
          });
          break;

        case (responseFindError?.body?.code === -3002):
          // "User cannot edit own role", -3002
          notification.error({
            message: 'Erro',
            description: 'Não é permitido alterar o próprio perfil',
          });
          break;

        case (responseFindError?.body.code === -3000):
          // "Role not found", -3000
          notification.error({
            message: 'Erro',
            description: 'Perfil não encontrado',
          });
          navigate('/roles/list');
          break;

        case (responseFindError?.body.code === -4001):
          break;

        case (genericFindError?.message?.includes('has been aborted')):
          break;

        case (responseFindError?.body?.code === -4000):
        default:
          // "Something unexpected happened", -4000
          notification.error({
            message: 'Erro',
            description: 'Não foi possivel encontrar os dados do perfil, tente novamente por favor',
          });
          break;
      }
    } else {
      setRoleName('Administrador do sistema');
    }

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: `/role/findAllUsers/${id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },

      query: {
        page: page - 1,
      },
    });

    try {
      response = await requestReference;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    setLoading(false);

    switch (true) {
      case (response?.body?.code === 3008): {
        // "Role users listed successfully", 3008
        const newData = response?.body?.users
          .filter(
            (d) => !responseRemoveUsers.find((r) => d.id === r.id),
          );

        setData(page === 1 ? [...responseAddUsers, ...newData] : newData);
        setPaginationInfo((previous) => {
          return ({
            ...previous,
            ...response?.body?.paginationInfo,
          });
        });
        setLoading(false);
        setLastPageSearched(page);
        setDefaultRole(response?.body?.role.defaultRole);
      } break;

      case (response?.body?.code === -3000):
        // "Role not found", -3000
        notification.error({
          message: 'Erro',
          description: 'Perfil não encontrado',
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Voce não tem permissão para executar esta ação.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        setLoading(false);
        message.error('Ocorreu um erro ao buscar a listagem, tente novamente');
        break;
    }
  };

  const handleSave = async (): Promise<void> => {
    const { id } = matchObject.params;

    const params = {
      id,
      addedUsersId: responseAddUsers.map((v) => v.id),
      removedUsersId: responseRemoveUsers.map((v) => v.id),
    };

    setLoading(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = put({
      url: '/role/editUsers',
      params,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    setLoading(false);

    switch (true) {
      case (response?.body?.code === 3009):
        // "Role users edited successfully", 3009
        notification.success({
          message: 'Sucesso',
          description: 'Modificações salvas.',
        });
        navigate('/roles/list');
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -3000):
        // "Role not found", -3000
        notification.error({
          message: 'Erro',
          description: 'Perfil não encontrado.',
        });
        break;

      case (responseError?.body?.code === -3013):
        // "Cannot remove users from default role", -3013
        notification.error({
          message: 'Erro',
          description: 'Não é permitido remover usuários de um perfil padrão.',
        });
        break;

      case (responseError?.body?.code === -3014):
        // "Logged user cannot add yourself to another role", -3014
        notification.error({
          message: 'Erro',
          description: 'Não é permitido adicionar a si mesmo a um perfil.',
        });
        break;

      case (responseError?.body?.code === -3015):
        // "Logged user cannot remove your role", -3015
        notification.error({
          message: 'Erro',
          description: 'Não é permitido remover a si mesmo de um perfil.',
        });
        break;

      case (responseError?.body?.code === -3016):
        // "Some users cannot be added to the role", -3016
        notification.error({
          message: 'Erro',
          description: 'Alguns usuários não puderam ser adicionados ao perfil.',
        });
        break;

      case (responseError?.body?.code === -3017):
        // "Some users cannot be removed from the role", -3017
        notification.error({
          message: 'Erro',
          description: 'Alguns usuários não puderam ser removidos do perfil.',
        });
        break;

      case (responseError?.body?.code === -3003):
        // "At least one admin should be on system", -3003
        notification.error({
          message: 'Erro',
          description: 'Deve existir ao menos um Administrador no sistema.',
        });
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Você não possui permissão para executar esta ação.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: 'Erro ao salvar as modificações.',
        });
        break;
    }
  };

  const loadUser = async (): Promise<void> => {
    const { id } = matchObject.params;

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: `/user/findAllByTypeAndDifferentRole/${id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 1012):
        // "Users by type listed successfully", 1012
        setAllActivatedUsers(response?.body.users);
        setUserModalOptions(
          response?.body.users.map(
            (v) => (
              <Select.Option
                key={v.id}
                value={v.id}
              >
                {v.name}
              </Select.Option>
            ),
          ),
        );
        break;

      case (response?.body?.code === -3000):
        // "Role not found", -3000
        notification.error({
          message: 'Erro',
          description: 'Perfil não encontrado.',
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: 'Você não possui permissão para executar esta ação.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Erro ao carregar a listagem de usuários responsáveis.',
        });
        break;
    }
  };

  const addUser = async (): Promise<void> => {
    let values;

    try {
      values = await formInstance.validateFields(['selectUser']);
    } catch (error) {
      console.log(error);
      return;
    }

    const id = Number.parseInt(values.selectUser, 10);

    const currentUserModalAdd = [
      ...responseRemoveUsers,
      ...allActivatedUsers,
    ].find((v) => v.id === id);

    const index = responseRemoveUsers.findIndex((v) => v.id === id);

    if (index === -1) {
      const temp = allActivatedUsers.filter((v) => v.id !== id);

      setAllActivatedUsers(temp);
      setResponseAddUsers([...responseAddUsers, currentUserModalAdd]);
      setUserModalOptions(
        [...temp, ...responseRemoveUsers]
          .map(
            (v) => (
              <Select.Option
                key={v.id}
                value={v.id}
              >
                {v.name}
              </Select.Option>
            ),
          ),
      );

      setVisibleModalAddUser(false);
    } else {
      const temp = responseRemoveUsers.filter((v) => v.id !== id);

      setResponseRemoveUsers(temp);
      setUserModalOptions(
        [...allActivatedUsers, ...temp]
          .map(
            (v) => (
              <Select.Option
                key={v.id}
                value={v.id}
              >
                {v.name}
              </Select.Option>
            ),
          ),
      );
      setVisibleModalAddUser(false);
    }
  };

  const removeUser = (): void => {
    const { id } = currentUserModalRemove;

    const index = responseAddUsers.findIndex((v) => v.id === id);

    if (index === -1) {
      setResponseRemoveUsers([...responseRemoveUsers, currentUserModalRemove]);
      setData(data.filter((v) => v.id !== id));
      setUserModalOptions(
        [
          ...responseRemoveUsers,
          ...allActivatedUsers,
          currentUserModalRemove,
        ].map(
          (v) => (
            <Select.Option
              key={v.id}
              value={v.id}
            >
              {v.name}
            </Select.Option>
          ),
        ),
      );
      setVisibleModalRemove(false);
    } else {
      setAllActivatedUsers([...allActivatedUsers, currentUserModalRemove]);
      setResponseAddUsers(responseAddUsers.filter((v) => v.id !== id));
      setUserModalOptions(
        [
          ...responseRemoveUsers,
          ...allActivatedUsers,
          currentUserModalRemove,
        ].map(
          (v) => (
            <Select.Option
              key={v.id}
              value={v.id}
            >
              {v.name}
            </Select.Option>
          ),
        ),
      );

      setVisibleModalRemove(false);
    }
  };

  const columns = [
    {
      title: <div className="table-titles">Nome</div>,
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: <div className="table-titles">E-mail</div>,
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: <div className="table-titles">Status</div>,
      dataIndex: 'status',
      key: 'status',

      render: (statuss) => (
        <div>
          <div className="status-color" style={{ backgroundColor: statusList[statuss].color }} />
          {statusList[statuss].name}
        </div>
      ),
    },
    ...(!defaultRole ? [{
      title: '',
      dataIndex: 'actions',
      key: 'actions',

      render: (text, record) => (
        <TrashCanIcon
          className="btn-action"
          onClick={
            (): void => {
              setCurrentUserModalRemove(record);
              setVisibleModalRemove(true);
            }
          }
        />
      ),
    }] : []),
  ];

  useEffect(() => {
    const runOnMount = async (): Promise<void> => {
      await loadUser();
      await fetchData(1);
    };

    runOnMount();

    return () => {
      setState(false);
    };
  }, []);

  useEffect(() => {
    fetchData(lastPageSearched);
  }, [responseAddUsers, responseRemoveUsers, lastPageSearched]);

  return (
    <div className="AssociateProfile">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        <Row gutter={24} className="title-container">
          <Col xs={{ span: 24 }} lg={{ span: 11 }}>
            <span className="page-title">Usuários do perfil</span>
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 13 }}>
            <div className="buttons-container">
              <Row gutter={24}>
                <Col xs={{ span: 24 }} md={{ span: 6 }}>
                  <Button
                    className="btn-previous"
                    onClick={
                      (): void => {
                        if (responseAddUsers.length || responseRemoveUsers.length) {
                          setVisibleModalCancel(true);
                        } else {
                          navigate('/roles/list');
                        }
                      }
                    }
                  >
                    Voltar
                  </Button>
                </Col>
                <Col xs={{ span: 24 }} md={{ span: 7 }}>
                  <Button
                    className="btn-primary"
                    onClick={handleSave}
                  >
                    Salvar
                  </Button>
                </Col>
                <Col xs={{ span: 24 }} md={{ span: 11 }}>
                  <Button
                    className="btn-primary"
                    type="primary"
                    onClick={
                      (): void => {
                        setVisibleModalAddUser(true);
                      }
                    }
                  >
                    Adicionar usuário
                  </Button>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        <Row gutter={24} className="list-container">
          <Col xs={{ span: 24 }}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={data}
              pagination={false}
              title={
                (): JSX.Element => {
                  return (<div className="table-title">{roleName}</div>);
                }
              }
              footer={
                (): JSX.Element => {
                  return (
                    <CustomPagination
                      paginationInfo={paginationInfo}
                      fetchFunc={fetchData}
                    />
                  );
                }
              }
            />
          </Col>
        </Row>

        <Modal
          wrapClassName="AssociateProfile-user-modal"
          title={<h3 style={{ margin: 0 }}>Adicionar usuário</h3>}
          visible={visibleModalAddUser}
          onOk={addUser}
          onCancel={
            (): void => {
              setVisibleModalAddUser(false);
            }
          }
          confirmLoading={loadingModalUser}
          footer={[
            <Button
              className="btn-ok"
              type="primary"
              key="submit"
              onClick={addUser}
            >
              Salvar
            </Button>,
          ]}
        >
          {
            visibleModalAddUser
            && (
              <Form
                scrollToFirstError
                form={formInstance}
              >
                <Form.Item
                  label=""
                  name="selectUser"
                  rules={[{ required: true, message: 'Por favor, selecione o usuário.' }]}
                  initialValue={undefined}
                >
                  <Select
                    showSearch
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Digite o usuário"
                    filterOption={
                      (input, option): any => option.props
                        .children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {userModalOptions}
                  </Select>
                </Form.Item>
              </Form>
            )
          }
        </Modal>

        <Modal
          wrapClassName="AssociateProfile-user-modal"
          title={<h3 style={{ margin: 0 }}>Remover usuário</h3>}
          visible={visibleModalRemove}
          onOk={removeUser}
          onCancel={
            (): void => {
              setVisibleModalRemove(false);
            }
          }
          confirmLoading={loadingModalRemove}
          footer={[
            <Button
              className="btn-cancel"
              key="back"
              onClick={
                (): void => {
                  setVisibleModalRemove(false);
                  setCurrentUserModalRemove({});
                }
              }
            >
              Cancelar
            </Button>,
            <Button
              className="btn-ok"
              type="primary"
              key="submit"
              onClick={removeUser}
            >
              Confirmar
            </Button>,
          ]}
        >
          <p>Deseja remover usuário?</p>
          <p className="user-container">
            <AccountIcon />
            <span>{currentUserModalRemove.name}</span>
          </p>
        </Modal>
        <Modal
          className="cancel-modal"
          visible={visibleModalCancel}
          onOk={
            (): void => {
              navigate('/roles/list');
            }
          }
          onCancel={
            (): void => {
              setVisibleModalCancel(false);
            }
          }
          okText="Sim"
          cancelText="Não"
          okType="danger"
          closable={false}
        >
          <div className="modal-div">
            <InfoCircleTwoTone style={{ color: '#faad14', fontSize: '22px', paddingRight: '8px' }} />
            Os dados alterados serão perdidos. Tem certeza que deseja cancelar?
          </div>
        </Modal>
      </Spin>
    </div>
  );
};

AssociateProfile.propTypes = {};

AssociateProfile.defaultProps = {};

export default AssociateProfile;
